import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import cn from 'classnames'

import { useAppSelector } from 'store'

import AppButton from 'components/App/AppButton'
import AppDialog from 'components/App/AppDialog'
import AppFlex from 'components/App/AppFlex'
import AppIcon from 'components/App/AppIcon'
import AppSelect from 'components/App/AppSelect'
import AppText from 'components/App/AppText'

import styles from './AppDatepicker.module.scss'

export type Props = {
  value: string
  type?: 'date' | 'datetime'
  placeholder?: string
  error?: string
  danger?: boolean
  disabled?: boolean
  dateFormat?: string
  timeFormat?: string
  onChange: (value: string) => void
}

type ViewMode = 'days' | 'months' | 'years'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'))

const AppDatepicker: React.FC<Props> = ({ value, type = 'date', placeholder, error, danger, disabled, dateFormat = 'DD/MM/YYYY', timeFormat = 'HH:mm', onChange }) => {
  const { t } = useTranslation()

  const isRtl = useAppSelector(({ $app }) => $app.isRtl)

  const [open, setOpen] = useState(false)

  const today = moment()

  const parsedValue = useMemo(() => {
    if (!value) {
      return null
    }

    if (type === 'datetime') {
      const dateTime = moment(value, moment.ISO_8601, true)
      return dateTime.isValid() ? dateTime : null
    }

    const date = moment(value, 'YYYY-MM-DD', true)
    return date.isValid() ? date : null
  }, [value, type])

  const [currentMonth, setCurrentMonth] = useState(today.month() + 1)
  const [currentYear, setCurrentYear] = useState(today.year())
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('days')
  const [yearPageStart, setYearPageStart] = useState(today.year() - (today.year() % 12))
  const [time, setTime] = useState({ hour: '09', minute: '00' })

  const initializeDraft = () => {
    const baseDate = parsedValue ? parsedValue.clone() : today.clone()

    setCurrentMonth(baseDate.month() + 1)
    setCurrentYear(baseDate.year())
    setSelectedDay(parsedValue ? parsedValue.date() : null)
    setViewMode('days')
    setYearPageStart(baseDate.year() - (baseDate.year() % 12))

    if (type === 'datetime') {
      setTime({
        hour: parsedValue ? parsedValue.format('HH') : '09',
        minute: parsedValue ? parsedValue.format('mm') : '00',
      })
    }
  }

  const handleOpen = () => {
    if (disabled) {
      return
    }

    initializeDraft()
    setOpen(true)
  }

  const handleCancel = () => {
    setOpen(false)
  }

  const startOfMonth = moment().year(currentYear).month(currentMonth - 1).startOf('month')
  const endOfMonth = moment().year(currentYear).month(currentMonth - 1).endOf('month')

  const daysToPrepend = startOfMonth.day()
  const daysToAppend = 6 - endOfMonth.day()

  const daysInMonth = Array.from({ length: endOfMonth.date() }, (_, i) => {
    const day = i + 1
    const isSelected = selectedDay === day

    return {
      value: startOfMonth.clone().add(i, 'days'),
      status: isSelected ? 'selected' : 'active',
    }
  })

  const prependDays = Array.from({ length: daysToPrepend }, (_, i) => ({
    value: startOfMonth.clone().subtract(i + 1, 'days'),
    status: 'inactive',
  })).reverse()

  const appendDays = Array.from({ length: daysToAppend }, (_, i) => ({
    value: endOfMonth.clone().add(i + 1, 'days'),
    status: 'inactive',
  }))

  const calendarDays = [...prependDays, ...daysInMonth, ...appendDays]
  const weekDays = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')]

  const emitValue = (day: number, month: number, year: number, nextTime = time) => {
    const date = moment().year(year).month(month - 1).date(day)

    if (type === 'datetime') {
      date.hour(Number(nextTime.hour))
      date.minute(Number(nextTime.minute))
      onChange(date.format('YYYY-MM-DDTHH:mm'))
      return
    }

    onChange(date.format('YYYY-MM-DD'))
  }

  const handleMonthNav = (direction: 'prev' | 'next') => () => {
    if (viewMode === 'years') {
      setYearPageStart(prev => direction === 'prev' ? prev - 12 : prev + 12)
      return
    }

    if (viewMode === 'months') {
      setCurrentYear(prev => direction === 'prev' ? prev - 1 : prev + 1)
      return
    }

    if (direction === 'prev') {
      if (currentMonth === 1) {
        setCurrentMonth(12)
        setCurrentYear(prev => prev - 1)
        return
      }

      setCurrentMonth(prev => prev - 1)
      return
    }

    if (currentMonth === 12) {
      setCurrentMonth(1)
      setCurrentYear(prev => prev + 1)
      return
    }

    setCurrentMonth(prev => prev + 1)
  }

  const handleDayPick = (day: number) => () => {
    setSelectedDay(day)
  }

  const handleMonthPick = (monthIndex: number) => () => {
    setCurrentMonth(monthIndex + 1)
    setViewMode('days')
  }

  const handleYearPick = (year: number) => () => {
    setCurrentYear(year)
    setViewMode('days')
  }

  const handleTimeChange = (part: 'hour' | 'minute') => (nextValue: string) => {
    const nextTime = {
      ...time,
      [part]: nextValue,
    }

    setTime(nextTime)
  }

  const handleApply = () => {
    if (!selectedDay) {
      return
    }

    emitValue(selectedDay, currentMonth, currentYear)
    setOpen(false)
  }

  const displayValue = parsedValue ? parsedValue.format(type === 'datetime' ? `${dateFormat} ${timeFormat}` : dateFormat) : ''
  const isActive = open || displayValue !== ''

  const renderTitle = () => {
    if (viewMode === 'years') {
      return <AppText center small bold>{yearPageStart} - {yearPageStart + 11}</AppText>
    }

    return (
      <AppFlex aCenter gap={4}>
        <AppFlex aCenter gap={1} className={styles.titlePart} onClick={() => setViewMode('months')}>
          <AppText center small bold>{t(startOfMonth.format('MMMM'))}</AppText>
          <AppIcon icon="chevron-down" size={12} primary />
        </AppFlex>

        <AppFlex aCenter gap={1} className={styles.titlePart} onClick={() => setViewMode('years')}>
          <AppText center small bold>{currentYear}</AppText>
          <AppIcon icon="chevron-down" size={12} primary />
        </AppFlex>
      </AppFlex>
    )
  }

  const renderMonths = () => (
    <AppFlex className={cn(styles.pickerGrid, styles.viewContainer)}>
      {MONTHS.map((monthKey, index) => {
        const isSelected = index + 1 === currentMonth
        const isCurrent = today.year() === currentYear && today.month() === index

        return (
          <AppFlex
            key={monthKey}
            center
            className={cn(styles.pickerCell, { [styles.selected]: isSelected, [styles.current]: isCurrent })}
            onClick={handleMonthPick(index)}
          >
            <AppText center small bold highlight={!isSelected} white={isSelected}>{t(monthKey)}</AppText>
          </AppFlex>
        )
      })}
    </AppFlex>
  )

  const renderYears = () => {
    const years = Array.from({ length: 12 }, (_, i) => yearPageStart + i)

    return (
      <AppFlex className={cn(styles.pickerGrid, styles.viewContainer)}>
        {years.map((yearItem) => {
          const isSelected = yearItem === currentYear
          const isCurrent = yearItem === today.year()

          return (
            <AppFlex
              key={yearItem}
              center
              className={cn(styles.pickerCell, { [styles.selected]: isSelected, [styles.current]: isCurrent })}
              onClick={handleYearPick(yearItem)}
            >
              <AppText center small bold highlight={!isSelected} white={isSelected}>{yearItem}</AppText>
            </AppFlex>
          )
        })}
      </AppFlex>
    )
  }

  const renderDays = () => (
    <AppFlex direction="column" className={styles.viewContainer}>
      <AppFlex row aCenter gap={2}>
        {weekDays.map(day => (
          <AppFlex key={day} padding={2} center className={styles.weekDay}>
            <AppText center xs uppercase highlight>{day}</AppText>
          </AppFlex>
        ))}
      </AppFlex>

      <AppFlex wrap aCenter gap={2} className={styles.daysContent}>
        {calendarDays.map((day, index) => (
          <AppFlex
            key={index}
            center
            className={cn(styles.day, styles[day.status])}
            onClick={day.status === 'active' || day.status === 'selected' ? handleDayPick(day.value.date()) : () => {}}
          >
            <AppText center small bold highlight={day.status !== 'selected'} white={day.status === 'selected'}>{day.value.date()}</AppText>
          </AppFlex>
        ))}
      </AppFlex>
    </AppFlex>
  )

  return (
    <AppFlex column gap={1}>
      <AppFlex
        aCenter
        className={cn(styles.wrapper, {
          [styles.active]: isActive,
          [styles.disabled]: disabled,
          [styles.error]: error && (error !== '' || danger),
        })}
        onClick={handleOpen}
      >
        <AppFlex fullHeight flex={1} style={{ position: 'relative', zIndex: 1 }}>
          <AppFlex className={cn(styles.value, { [styles.placeholder]: displayValue === '' })}>
            <AppText muted={displayValue === ''}>{displayValue || placeholder || ''}</AppText>
          </AppFlex>
        </AppFlex>

        <AppIcon icon="chevron-down" small />
      </AppFlex>

      {error && error !== '' ? (
        <AppFlex>
          <AppText small danger>{error}</AppText>
        </AppFlex>
      ) : null}

      <AppDialog
        open={open}
        onClose={handleCancel}
        actions={(
          <AppFlex gap={2} padding={4}>
            <AppButton outlined flex={1} onClick={handleCancel}>{t('Cancel')}</AppButton>
            <AppButton flex={1} disabled={!selectedDay} onClick={handleApply}>{t('Apply')}</AppButton>
          </AppFlex>
        )}
      >
        <AppFlex column gap={4} className={styles.dialogContent}>
          <AppFlex aCenter jBetween>
            <AppFlex center className={styles.arrow} onClick={handleMonthNav('prev')}>
              <AppIcon icon="chevron-left2" size={24} primaryColor rotate={isRtl} />
            </AppFlex>

            {renderTitle()}

            <AppFlex center className={cn(styles.arrow, { [styles.rtl]: isRtl })} onClick={handleMonthNav('next')}>
              <AppIcon icon="chevron-right2" size={24} primaryColor rotate={isRtl} />
            </AppFlex>
          </AppFlex>

          {viewMode === 'days' && renderDays()}
          {viewMode === 'months' && renderMonths()}
          {viewMode === 'years' && renderYears()}

          {type === 'datetime' ? (
            <AppFlex className={styles.timeWrap} aCenter gap={2}>
              <AppSelect
                value={time.hour}
                options={HOURS.map(hour => ({ value: hour, label: hour }))}
                onChange={handleTimeChange('hour')}
              />
              <AppFlex center className={styles.timeSeparator}>
                <AppText small bold>:</AppText>
              </AppFlex>
              <AppSelect
                value={time.minute}
                options={MINUTES.map(minute => ({ value: minute, label: minute }))}
                onChange={handleTimeChange('minute')}
              />
            </AppFlex>
          ) : null}
        </AppFlex>
      </AppDialog>
    </AppFlex>
  )
}

export default AppDatepicker
